import { useMemo } from "react"
import { useOrganizationPermissions } from "../useOrganizationPermissions"
import type {
  UseAuthorizedContentParams,
  UseAuthorizedContentReturn,
} from "./useAuthorizedContent.types"

/**
 * Hook to check if organization user has permission for specific content
 * @param params - Resource and action to check, or a list of alternative
 * resource/action pairs (`anyOf`) when authorization can be satisfied by any
 * one of several permissions
 * @returns Authorization status and loading state
 */
export const useAuthorizedContent = ({
  resource,
  action,
  anyOf,
}: UseAuthorizedContentParams): UseAuthorizedContentReturn => {
  const { hasPermission, isLoading, error } = useOrganizationPermissions()

  const isAuthorized = useMemo(() => {
    if (anyOf && anyOf.length > 0) {
      return anyOf.some((permission) =>
        hasPermission(permission.resource, permission.action)
      )
    }
    return resource !== undefined && action !== undefined
      ? hasPermission(resource, action)
      : false
  }, [hasPermission, resource, action, anyOf])

  return {
    isAuthorized,
    isLoading,
    error,
  }
}
